import { EVerticalNames } from '@constants';
import { IUser } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Avatar, Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { InfoItem } from './InfoItem';
import { Row, TagsWrapper, TitleWrapper, Wrapper } from './StudentDetail.styles';
import { StudentTutor } from './StudentTutor';

import { Icon } from '@/components/Icon';
import { useUserInfoByUser, useUserOnboarding } from '@/store';

type Props = {
  student: IUser;
};

const AVATAR_SIZE = 56;

export const StudentDetail: React.FC<Props> = ({ student }) => {
  const { userOnboarding: onboarding } = useUserOnboarding({ userId: student?._id });
  const contract = onboarding?.contract;
  const { t } = useTranslation(['DefaultTranslations', 'StudentProfileConfigurationTranslations']);
  const { userInfo: studentUserInfo } = useUserInfoByUser(student?._id || '');
  const {
    _id,
    name,
    lastName,
    secondLastName,
    avatar,
    email,
    tags,
    birthday,
    createdAt,
    regionConfig,
    phone,
    legalId,
    legalIdType,
    hubspotId,
    academy
  } = student;
  const isFpStudent = academy?.slug === EVerticalNames.FP;

  const handleDownloadContract = () => {
    if (!contract?.contractFile?.url) return;
    const downloadLink = document.createElement('a');
    downloadLink.href = contract.contractFile.url;
    downloadLink.download = contract.contractFile.name ?? 'contract.pdf';
    document.body.appendChild(downloadLink);
    downloadLink.click();
  };

  const renderAddress = () => {
    if (!isFpStudent || !studentUserInfo?.address) return null;

    return (
      <InfoItem title={t('StudentProfileConfigurationTranslations:ADDRESS_DATA')}>
        {[studentUserInfo.streetType, studentUserInfo.address, studentUserInfo.streetNumber]
          .filter(Boolean)
          .join(' ')}
      </InfoItem>
    );
  };

  const renderAddressDetails = () => {
    if (!isFpStudent || !studentUserInfo?.address) return null;

    const hasAdditionalData =
      studentUserInfo.staircase ||
      studentUserInfo.floor ||
      studentUserInfo.door ||
      studentUserInfo.postalCode;

    if (!hasAdditionalData) return null;

    return (
      <InfoItem title={t('StudentProfileConfigurationTranslations:ADDRESS_DATA')}>
        {[
          studentUserInfo.staircase &&
            `${t('StudentProfileConfigurationTranslations:STAIRCASE')}: ${
              studentUserInfo.staircase
            }`,
          studentUserInfo.floor &&
            `${t('StudentProfileConfigurationTranslations:FLOOR')}: ${studentUserInfo.floor}`,
          studentUserInfo.door &&
            `${t('StudentProfileConfigurationTranslations:DOOR')}: ${studentUserInfo.door}`,
          studentUserInfo.postalCode &&
            `${t('StudentProfileConfigurationTranslations:POSTAL_CODE')}: ${
              studentUserInfo.postalCode
            }`
        ]
          .filter(Boolean)
          .join(', ')}
      </InfoItem>
    );
  };

  return (
    <Wrapper vertical gap={24} align="flex-start">
      <TitleWrapper>{t('STUDENT_INFO')}</TitleWrapper>
      <Avatar size={AVATAR_SIZE} src={avatar?.url}>
        {`${(name ?? '?').charAt(0).toUpperCase()}${(lastName ?? '?').charAt(0).toUpperCase()}`}
      </Avatar>

      <InfoItem title={t('TAG_ASSOCIATED')}>
        <TagsWrapper gap={8}>
          {tags.map(tag => (
            <Tag key={tag._id}>{tag.name}</Tag>
          ))}
        </TagsWrapper>
      </InfoItem>

      <Row style={{ gridTemplateColumns: secondLastName ? '1fr 1fr 1fr' : '1fr 1fr' }}>
        <InfoItem title={t('StudentProfileConfigurationTranslations:NAME')}>{name || '-'}</InfoItem>
        <InfoItem title={t('StudentProfileConfigurationTranslations:SURNAME')}>
          {lastName || '-'}
        </InfoItem>
        {secondLastName && (
          <InfoItem title={t('StudentProfileConfigurationTranslations:SECOND_SURNAME')}>
            {secondLastName}
          </InfoItem>
        )}
      </Row>

      <InfoItem title={t('EMAIL')}>{email}</InfoItem>

      {phone && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:PHONE')}>{phone}</InfoItem>
      )}

      <Row
        style={{
          gridTemplateColumns: isFpStudent && studentUserInfo?.nationality ? '1fr 1fr' : '1fr'
        }}>
        <InfoItem title={t('StudentProfileConfigurationTranslations:IDENTIFICATION_DOCUMENT')}>
          {legalIdType && legalId ? `${legalIdType} - ${legalId}` : legalId ?? '-'}
        </InfoItem>
        {isFpStudent && studentUserInfo?.nationality && (
          <InfoItem title={t('StudentProfileConfigurationTranslations:NATIONALITY')}>
            {t(`DefaultTranslations:${studentUserInfo.nationality}`)}
          </InfoItem>
        )}
      </Row>

      <Row
        style={{
          gridTemplateColumns:
            isFpStudent && studentUserInfo?.socialSecurityNumber ? '1fr 1fr' : '1fr'
        }}>
        {isFpStudent && studentUserInfo?.socialSecurityNumber && (
          <InfoItem title={t('SOCIAL_SECURITY_NUMBER')}>
            {studentUserInfo.socialSecurityNumber}
          </InfoItem>
        )}
        <InfoItem title={t('HUBSPOT_ID')}>{hubspotId || '-'}</InfoItem>
      </Row>

      {birthday && (
        <InfoItem title={t('BIRTHDAY')}>{dateProvider.format(birthday, 'dd/MM/yyyy')}</InfoItem>
      )}
      {studentUserInfo?.gender && <InfoItem title={t('GENDER')}>{studentUserInfo.gender}</InfoItem>}
      {studentUserInfo?.countryBirth && (
        <InfoItem title={t('BORN_COUNTRY')}>{studentUserInfo.countryBirth}</InfoItem>
      )}

      {isFpStudent && studentUserInfo?.birthProvince && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:BIRTH_PROVINCE')}>
          {studentUserInfo.birthProvince}
        </InfoItem>
      )}
      {isFpStudent && studentUserInfo?.birthMunicipality && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:BIRTH_MUNICIPALITY')}>
          {studentUserInfo.birthMunicipality}
        </InfoItem>
      )}
      {isFpStudent && (studentUserInfo?.birthLocality || studentUserInfo?.birthLocalityForeign) && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:BIRTH_LOCALITY')}>
          {studentUserInfo.birthLocality || studentUserInfo.birthLocalityForeign}
        </InfoItem>
      )}

      {studentUserInfo?.countryResidence && (
        <InfoItem title={t('RESIDENCE_COUNTRY')}>{studentUserInfo.countryResidence}</InfoItem>
      )}
      <>
        {renderAddress()}
        {renderAddressDetails()}
      </>

      {isFpStudent && studentUserInfo?.province && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:PROVINCE')}>
          {studentUserInfo.province}
        </InfoItem>
      )}
      {isFpStudent && studentUserInfo?.city && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:MUNICIPALITY')}>
          {studentUserInfo.city}
        </InfoItem>
      )}
      {isFpStudent && studentUserInfo?.residenceLocality && (
        <InfoItem title={t('StudentProfileConfigurationTranslations:RESIDENCE_LOCALITY')}>
          {studentUserInfo.residenceLocality}
        </InfoItem>
      )}

      <InfoItem title={t('TUTOR')}>
        <StudentTutor studentId={_id} />
      </InfoItem>

      <InfoItem title={t('STUDENT_FIRST_PAYMENT')}>
        {dateProvider.format(createdAt, 'dd/MM/yyyy')}
      </InfoItem>

      {regionConfig && <InfoItem title={t('REGION')}>{t(`REGION_${regionConfig}`)}</InfoItem>}

      <InfoItem title={t('CONTRACT')}>
        <Button
          type="text"
          size="small"
          onClick={handleDownloadContract}
          icon={<Icon i="DocumentSigned" />}
          disabled={!contract?.contractFile?.url}>
          {t('DOWNLOAD_CONTRACT')}
        </Button>
      </InfoItem>
    </Wrapper>
  );
};
